/**
 * ראשי — REDESIGN §5, the business skeleton of §4.
 *
 * One reading order, desktop and mobile alike:
 *
 *   1. `מה קורה` — the written summary (§3), plain ground, above every figure.
 *   2. **The one number** — `יתרה זמינה`, the only 36px+ figure on the screen, with its inputs on
 *      one small line beneath it and the 30-day forecast as its comparison chip.
 *   3. `עכשיו` / `השבוע` / `החודש` — the urgency lists (§2), all three built from ONE row
 *      component, empty buckets absent.
 *   4. `אפשר לחכות` — collapsed to a single line, opens in place.
 *   5. The quiet detail strip — תזרים · גבייה · מע"מ · הוצאות · תפעול · הלוואות עסקיות, at most 5
 *      content lines each, every block linking to its screen.
 *
 * Gone from this screen (§4 "Removed from home"), because the strip or another screen already owns
 * them: the four-tile row, the alerts card, the 12-month revenue column chart, the recent-
 * transactions table and the bordered card grid the whole screen used to be. The blocks that
 * remain sit on the page ground; only a block you can act on or open earns a border (§8).
 */

import { useMemo } from 'react';
import type { SummaryResponse } from '../../../api-types.ts';
import { fmtDate, fmtMoney, fmtShortDate, mask, monthName } from '../format.ts';
import { periodLabel } from '../dates.ts';
import { he } from '../i18n/he.ts';
import { CategoryIcon, EmptyState as KitEmptyState } from '../../../../shared/ui/src/index.tsx';
import { HeroFigure, LaterSection, NarrativeBlock, StripBlock, StripLine, UrgencySection } from '../components/insight.tsx';
import { Chip, Mask, Money, Num } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { BalanceLine, balanceSummary } from '../charts/BalanceLine.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { Link } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';
import { asOfOf, insightOf, type SummaryWithInsight, type UrgencyLabels } from '../insight.ts';
import { filterLoans, loanTotals, splitOpen, type LoansPayload } from '../loans.ts';

/** The Hebrew and the routes `businessUrgencyInputs()` needs; `i18n/he.ts` stays the one place copy lives. */
const URGENCY_LABELS: UrgencyLabels = {
  vatReport: (period) => he.urgency.vatReport(period),
  vatHref: (period) => buildPath('vat', null, { period }),
  taxHref: buildPath('vat'),
  cashflowHref: buildPath('cashflow'),
  invoicesHref: buildPath('invoices'),
  overdueHref: buildPath('invoices', null, { status: 'overdue' }),
  invoicesOverdue: (n) => he.urgency.invoicesOverdue(n),
  noInvoice: (n) => he.urgency.noInvoice(n),
  noInvoiceHref: buildPath('expenses', null, { noinvoice: '1' }),
  forecastNegative: he.urgency.forecastNegative,
  connectionExpired: (provider) => he.urgency.connectionExpired(provider),
  connectionsHref: buildPath('settings', 'connections'),
  dataStale: he.urgency.dataStale,
};

export function HomeScreen({ summary }: { summary: SummaryResponse }) {
  const { api } = useApp();
  const asOf = asOfOf(summary);
  const insight = useMemo(() => insightOf(summary as SummaryWithInsight, URGENCY_LABELS), [summary]);

  // The loans block of the strip (REDESIGN §6: "Also a 4-line summary block in the home detail
  // strip"). It is the only extra request the home screen makes, and it never blocks a render.
  const loans = useQuery<LoansPayload>(cacheKey('loans', { asOf }), () => api.loans(asOf), { scopes: ['accounts', 'summary'] });

  const balance = summary.balance;
  const tax = summary.tax;
  const collection = summary.collection;
  const ops = summary.ops;
  const groups = insight.urgency.groups;
  const dated = groups.filter((group) => group.bucket !== 'later');
  const later = groups.find((group) => group.bucket === 'later');

  return (
    <div className="home2">
      {/* A payload without `narrative`/`urgency` is a server bug (REDESIGN §3/§9). The screen still
          renders, from the client reconstruction — but it says so, so nobody signs off on it. */}
      {insight.source === 'client' ? (
        <div className="banner warn" role="status">
          <Icon name="alertTri" className="ic sm" />
          <span>{he.urgency.clientFallback}</span>
        </div>
      ) : null}

      {/* 1 ─ מה קורה */}
      <NarrativeBlock narrative={insight.narrative} />

      {/* 2 ─ the one number. No third sub-line: `balance.note` ("הוצאות הכרטיס … מחוץ לחלון") is
          the forecast-exclusion footnote — precisely the coverage-footnote paragraph REDESIGN §4
          deleted from the personal home. It still lives on #cashflow, where the exclusions do. */}
      <HeroFigure
        label={he.home.heroLabel}
        value={<Money agorot={balance.availableAgorot} />}
        inputs={
          <>
            {he.home.heroTitle} <Mask>{mask(balance.account?.numberLast4)}</Mask>
            {balance.forecastAgorot === null ? null : (
              <>
                {' · '}
                {he.home.forecastTo(fmtShortDate(balance.forecastDate ?? '', asOf))} <Money agorot={balance.forecastAgorot} />
              </>
            )}
          </>
        }
        chip={
          balance.forecastDeltaAgorot === null ? null : (
            <Chip tone={balance.forecastDeltaAgorot < 0 ? 'warn' : 'good'} icon={balance.forecastDeltaAgorot < 0 ? 'arrowDown' : 'arrowUp'}>
              {fmtMoney(balance.forecastDeltaAgorot, 0, true)} {he.home.vsToday}
            </Chip>
          )
        }
      />

      {/* 3 ─ the urgency lists */}
      {dated.length === 0 && !later ? (
        <div className="fk-section">
          <KitEmptyState illustration="noAlerts" title={he.urgency.emptyTitle} body={he.urgency.emptyBody} variant="inline" />
        </div>
      ) : null}
      {dated.map((group) => (
        <UrgencySection key={group.bucket} group={group} asOf={asOf} limit={group.bucket === 'now' ? undefined : 3} />
      ))}

      {/* 4 ─ אפשר לחכות */}
      {later ? <LaterSection group={later} asOf={asOf} /> : null}

      <hr className="fk-rule" />

      {/* 5 ─ the quiet detail strip */}
      <h2 className="fk-section-title strip-title">{he.strip.title}</h2>
      <div className="fk-strip">
        <StripBlock
          title={he.strip.cashflow}
          to={buildPath('cashflow')}
          linkLabel={he.strip.cashflowLink}
          icon={<CategoryIcon glyph="arrows" soft size={16} />}
        >
          <StripLine label={he.home.income} value={<Money agorot={summary.month.incomeNetAgorot} />} />
          <StripLine label={he.home.expenses} value={<Money agorot={-summary.month.expenseNetAgorot} />} />
          <StripLine label={he.home.profit} value={<Money agorot={summary.month.profitAgorot} />} />
          <div className="strip-chart">
            <BalanceLine
              points={balance.series}
              events={balance.events}
              asOf={asOf}
              height={72}
              compact
              summary={balanceSummary(balance.availableAgorot ?? 0, balance.forecastAgorot ?? 0, balance.forecastDate ?? '', asOf)}
              table={{ head: [], rows: [], caption: '' }}
            />
          </div>
          <div className="fk-meta">{periodLabel(summary.period)}</div>
        </StripBlock>

        <StripBlock
          title={he.strip.collection}
          to={buildPath('invoices')}
          linkLabel={he.strip.collectionLink}
          icon={<CategoryIcon glyph="receipt" soft size={16} />}
        >
          <StripLine
            label={he.home.open}
            value={
              <>
                <Money agorot={collection.openAgorot} /> <span className="note">({collection.openCount})</span>
              </>
            }
          />
          <StripLine
            label={he.invoices.overdue}
            value={
              <>
                <Money agorot={collection.overdueAgorot} /> <span className="note">({collection.overdueCount})</span>
              </>
            }
          />
          {collection.topDebtors.slice(0, 2).map((debtor) => (
            <StripLine key={debtor.customerId} label={debtor.customerName} value={<Money agorot={debtor.openAgorot} />} />
          ))}
        </StripBlock>

        <StripBlock
          title={he.strip.tax}
          to={tax.period ? buildPath('vat', null, { period: tax.period.id }) : buildPath('vat')}
          linkLabel={he.strip.taxLink}
          icon={<CategoryIcon glyph="landmark" soft size={16} />}
        >
          {tax.period ? (
            <>
              <StripLine label={he.home.outputVat} value={<Money agorot={tax.outputVatAgorot} />} />
              <StripLine label={he.home.inputVat} value={<Money agorot={-tax.inputVatAgorot} />} />
              <StripLine label={tax.period.status === 'paid' ? he.home.paid : he.home.toPay} value={<Money agorot={tax.netVatAgorot} />} />
              <div className="fk-meta">
                {tax.period.label}
                {tax.dueDate ? (
                  <>
                    {' · '}
                    <Num>{fmtDate(tax.dueDate)}</Num>
                  </>
                ) : null}
              </div>
            </>
          ) : (
            <div className="fk-meta">{he.vat.notConfigured}</div>
          )}
        </StripBlock>

        <StripBlock
          title={he.strip.expenses}
          to={buildPath('expenses')}
          linkLabel={he.strip.expensesLink}
          icon={<CategoryIcon glyph="bag" soft size={16} />}
        >
          <StripLine label={he.home.expenses} value={<Money agorot={-summary.month.expenseNetAgorot} />} />
          <StripLine label={he.app.total} value={<Num>{summary.month.expenseCount}</Num>} />
          {tax.nonDeductibleCount ? (
            <StripLine
              label={
                <>
                  <Icon name="flag" className="sm" /> {he.home.nonDeductibleCount(tax.nonDeductibleCount)}
                </>
              }
              value={<Money agorot={-tax.nonDeductibleVatAgorot} />}
            />
          ) : null}
          <div className="fk-meta">{monthName(summary.period.lastMonth)}</div>
        </StripBlock>

        <StripBlock
          title={he.strip.ops}
          to={buildPath('ops')}
          linkLabel={he.strip.opsLink}
          icon={<CategoryIcon glyph="hammer" soft size={16} />}
        >
          <StripLine label={he.home.openCalls} value={<Num>{ops.openCalls}</Num>} />
          <StripLine label={he.home.activeSubs} value={<Num>{ops.activeSubscriptions}</Num>} />
          <StripLine label={he.home.mrr} value={<Money agorot={ops.mrrAgorot} />} />
          {ops.source.mode === 'demo' ? <div className="fk-meta">{he.home.opsTag}</div> : null}
        </StripBlock>

        <LoansStrip payload={loans.data} asOf={asOf} />
      </div>

      <div className="fk-meta home2__foot">
        <Link className="link-btn" to={buildPath('cashflow')}>
          {he.strip.recentLink} <Icon name="arrowLeft" className="sm" />
        </Link>
      </div>
    </div>
  );
}

/** REDESIGN §6 — the 4-line loans block of the detail strip. */
function LoansStrip({ payload, asOf }: { payload: LoansPayload | undefined; asOf: string }) {
  const rows = payload ? splitOpen(filterLoans(payload.rows, 'business')).open : [];
  const totals = loanTotals(rows, asOf);
  return (
    <StripBlock title={he.strip.loans} to={buildPath('loans')} linkLabel={he.strip.loansLink} icon={<CategoryIcon glyph="loan" soft size={16} />}>
      {rows.length === 0 ? (
        <div className="fk-meta">{payload ? he.strip.noLoans : he.app.loading}</div>
      ) : (
        <>
          <StripLine label={he.loans.totalDebt} value={<Money agorot={totals.balanceAgorot} />} />
          <StripLine
            label={he.loans.monthly}
            value={
              <>
                <Money agorot={totals.monthlyAgorot} />
                {totals.monthlyEstimated ? <span className="note"> {he.loans.estimated}</span> : null}
              </>
            }
          />
          <StripLine label={he.loans.weightedRate} value={totals.weightedRatePct === null ? he.app.none : <Num>{`${totals.weightedRatePct}%`}</Num>} />
          <StripLine label={he.loans.yearInterest} value={<Money agorot={-totals.yearInterestAgorot} />} />
        </>
      )}
    </StripBlock>
  );
}
